import React, { useCallback, useEffect, useMemo } from 'react';
import DataGrid from 'components/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import { Form, FormInstance, notification, Popconfirm, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from 'react-query';
import ButtonOperation from 'components/ButtonOperation';
import {
  createThirdPartyBkAccount,
  deleteThirdPartyBkAccount,
  getThirdPartyBkAccountListByTpid,
  TpBankAccount,
  TpBankAccountResponse2,
  updateThirdPartyBkAccount,
} from 'services/thirdParty';
import DeleteButton from 'components/DeleteButton';
import Text from 'antd/lib/typography/Text';
import ThirdPartyBkAccountStatus from './ThirdPartyBkAccountStatusButton';
import BankAccountsListModal from './BankAccountsModal';
import { indexBy, toDatePickerDate } from 'utils/utils2';
import moment from 'moment';
import ModalControllerNV from 'components/ModalControllerNV';

type BankAccountsListProps = {
  form: FormInstance;
  tpid: number;
  onClose?: any;
  countcode: string;
  currcode: string;
  showAsModal?: boolean;
};

const BankAccountsList = (props: BankAccountsListProps) => {
  const { form, tpid, showAsModal, countcode, currcode } = props;
  const fieldName = 'bankAccountsComponents';

  const { data: Accounts, refetch } = useQuery(
    ['getThirdPartyBkAccountListAction', { tpid: tpid }],
    () =>
      getThirdPartyBkAccountListByTpid({ tpid: tpid }).then(
        (res) => res.list || []
      ),
    { enabled: !!tpid }
  );

  useEffect(() => {
    if (Accounts) {
      form.setFieldsValue({
        bacc: Accounts.map((bacc) => ({
          bkstartdate: toDatePickerDate(bacc.bkstartdate),
          // tpbkid: bacc.tpbkid,
          // bkaccountkey:bacc.bkaccountkey,
          // bkaccountnum:bacc.bkaccountnum,
          // bkbranchcode:bacc.bkbranchcode,
          // bkcode:bacc.bkcode,
        })),
      });
      refetch();
    } else {
      form.setFieldsValue({ [fieldName]: [] });
    }
  }, [form, Accounts, refetch]);

  const onSave = useCallback(
    (record: any, action?: string) => {
      action === 'create'
        ? createThirdPartyBkAccount({
            bkrequest: {
              ...record,
              bkstartdate: moment(record.bkstartdate).format('YYYY-MM-DD'),
              tpid,
              countcode,
            },
            tpid: tpid,
          }).then((response) => {
            if (response.code === '200') {
              notification.success({
                message: response.message,
              });
              refetch();
            }
          })
        : updateThirdPartyBkAccount({
            bkrequest: { ...record, countcode },
            tpbkid: Number(record.tpbkid),
            tpid: tpid,
          }).then((response) => {
            if (response.code === '200') {
              notification.success({
                message: response.message,
              });
              refetch();
            }
          });
    },
    [refetch, tpid]
  );
  // eslint-disable-next-line no-unused-vars
  const getName = useCallback(
    (name: string) => (record: TpBankAccount) => {
      const functions = form.getFieldValue(fieldName);
      const index = indexBy(functions ?? [], 'tpbkid')[record.tpbkid];

      return [fieldName, index, name];
    },
    [form]
  );
  const columns = useMemo<DataGridColDef<TpBankAccountResponse2>[]>(
    () => [
      {
        dataIndex: 'bktypelabel',
        width: 100,
        render: (_value, _record, index) => (
          <ModalControllerNV
            modal={({ setVisible, visible }) => (
              <BankAccountsListModal
                showAsModal={showAsModal}
                countcode={countcode}
                visible={visible}
                onClose={() => {
                  setVisible(false);
                  refetch();
                }}
                onSave={(value) => {
                  onSave(value);
                  setVisible(false);
                }}
                tpid={tpid}
                record={_record}
                initadd={initialValue}
                currcode={_record.currcode}
              />
            )}
            content={({ setVisible, visible }) => (
              <a onClick={() => _record.bkstatus !== 'END' && setVisible(true)}>
                {_record.bktypelabel}
              </a>
            )}
          />
        ),
      },
      {
        dataIndex: 'tpbankname',
        width: 300,
        render: (_value, _record, index) => (
          <ModalControllerNV
            modal={({ setVisible, visible }) => (
              <BankAccountsListModal
                showAsModal={showAsModal}
                countcode={countcode}
                visible={visible}
                onClose={() => {
                  setVisible(false);
                  refetch();
                }}
                onSave={(value) => {
                  onSave(value);
                  setVisible(false);
                }}
                tpid={tpid}
                record={_record}
                initadd={initialValue}
                currcode={_record.currcode}
              />
            )}
            content={({ setVisible, visible }) => (
              <a onClick={() => _record.bkstatus !== 'END' && setVisible(true)}>
                {_record.tpbankname}
              </a>
            )}
          />
        ),
      },
      {
        dataIndex: 'bkbranchename',
        width: 200,
      },

      {
        dataIndex: 'tpiban',
        width: 300,
        render: (_value, _record, index) => (
          <ModalControllerNV
            modal={({ setVisible, visible }) => (
              <BankAccountsListModal
                showAsModal={showAsModal}
                countcode={countcode}
                visible={visible}
                onClose={() => {
                  setVisible(false);
                  refetch();
                }}
                onSave={(value) => {
                  onSave(value);
                  setVisible(false);
                }}
                tpid={tpid}
                record={_record}
                initadd={initialValue}
                currcode={_record.currcode}
              />
            )}
            content={({ setVisible, visible }) => (
              <a onClick={() => _record.bkstatus !== 'END' && setVisible(true)}>
                {_record.tpiban}
              </a>
            )}
          />
        ),
      },
      {
        dataIndex: 'tpcbtype',
      },
      {
        dataIndex: 'currcode',
        width: 80,
      },

      {
        dataIndex: 'bkstartdate',

        render: (value, record) => moment(value).format('YYYY-MM-DD'),
      },
      {
        dataIndex: 'bkenddate',

        render: (_value, _record, index) => <Text>{_value}</Text>,
      },

      {
        dataIndex: 'bkstatusdate',

        render: (_value, _record, index) => <Text>{_value}</Text>,
      },

      {
        dataIndex: 'bkstatuslabel',

        render: (_value, _record, index) => (
          <span>
            {_record.bkstatus.split(',').map((tag) => {
              let color;

              if (tag === 'VALID') {
                color = 'green';
              }
              if (tag === 'DISABLED') {
                color = ' #ffcc00';
              }
              if (tag === 'END') {
                color = 'red';
              }

              return (
                <Tag color={color} key={tag}>
                  {_record.bkstatuslabel}
                </Tag>
              );
            })}
          </span>
        ),
      },

      {
        dataIndex: 'actions',
        key: 'actions',
        render: (_, _record) => (
          <>
            <ThirdPartyBkAccountStatus
              tpid={_record.tpid}
              tpbkid={_record.tpbkid}
              status={_record.bkstatus}
              refetch={refetch}
            />
            {localStorage.getItem('userrole') === 'ADMIN' && (
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => {
                  deleteThirdPartyBkAccount({
                    tpbkid: _record.tpbkid,
                    tpid: _record.tpid,
                  }).then((res) => {
                    notification.success({ message: res.message });
                    refetch();
                  });
                }}
              >
                <DeleteButton
                  onlyIcon
                  style={{ height: '40px', width: '40px' }}
                />
              </Popconfirm>
            )}
          </>
        ),
        fixed: 'right',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSave, refetch, showAsModal, tpid]
  );

  return (
    <Form form={form}>
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }) => {
          return (
            <DataGrid
              rowKey="tpbkid"
              dataSource={Accounts}
              columns={
                !showAsModal
                  ? columns
                  : columns.filter((cl) => cl.key !== 'actions')
              }
              hideFilters
              extraActions={
                <ModalControllerNV
                  modal={({ setVisible, visible }) => (
                    <BankAccountsListModal
                      showAsModal={showAsModal}
                      countcode={countcode}
                      currcode={currcode}
                      visible={visible}
                      onClose={() => {
                        setVisible(false);
                        refetch();
                      }}
                      onSave={(value) => {
                        onSave(value, 'create');
                        setVisible(false);
                      }}
                      tpid={tpid}
                      initadd={initialValue}
                    />
                  )}
                  content={({ setVisible, visible }) => (
                    <ButtonOperation
                      type="primary"
                      icon={<PlusOutlined />}
                      comcode="addBK"
                      onClick={(ev) => {
                        setVisible(true);
                        ev.stopPropagation();
                      }}
                    />
                  )}
                />
              }
            />
          );
        }}
      </Form.Item>
    </Form>
  );
};
const initialValue = {
  bkenddate: '',
  bkstartdate: moment() as any,
  bkstatus: '',
  bkstatusdate: '',
  bktype: '',
  currcode: '',
  tpbankname: '',
  tpbic: '',
  tpbkid: 0,
  tpcbdate: '',
  tpcbkey: '',
  tpcbname: '',
  tpcbnum: '',
  tpcbtype: '',
  tpiban: '',
  tpid: 0,
};
export default BankAccountsList;
